import { FC, useEffect } from 'react'

import { EmbedSessionError } from 'components/pages/PurchaseFlow/_main/EmbedSessionError/EmbedSessionError'
import LoadingPage from 'components/pages/General/LoadingPage/LoadingPage'
import { PurchaseFlowRevamp } from 'components/pages/PurchaseFlowRevamp'
import { useUserData } from 'components/contexts/UserDataContext'
import { FeatureFlag, useFeatureFlag, useFeatureFlagsReadyFor } from 'utils/featureFlags'
import { markOrderFlowChosen } from 'utils/analytics/orderFlowLoad'
import { useFlagUserId } from 'utils/featureFlags/useFlagUserId'
import { useEmbedSessionBootstrap } from 'utils/embedSession/useEmbedSessionBootstrap'
import { logger } from 'utils/logging'

import { EmbedPurchaseFlowGuard } from '../EmbedPurchaseFlowGuard/EmbedPurchaseFlowGuard'
import { LegacyEmbedPurchaseFlow } from '../LegacyEmbedPurchaseFlow/LegacyEmbedPurchaseFlow'

/** When the embed first waited for the broker. Module-level: the loading gates above unmount this switch while the session activates. */
let waitStartedAt: number | null = null

/**
 * Picks v2 or legacy for the embed once the broker is known. The broker is identified to the flag service only
 * after the bootstrap exchange, and the exchange consumes the launch token, so a flow chosen on the anonymous
 * value and swapped afterwards would start without its session.
 */
export const EmbedPurchaseFlowSwitch: FC = () => {
  const { status } = useEmbedSessionBootstrap()
  const { baseUserData } = useUserData()
  const brokerKnown = status === 'active' && !!baseUserData
  const flagUser = useFlagUserId()
  const flagsReady = useFeatureFlagsReadyFor(brokerKnown ? flagUser : undefined)
  const allowPfRevamp = useFeatureFlag(FeatureFlag.ALLOW_PF_REVAMP)
  waitStartedAt ??= Date.now()
  const isDecided = status === 'active' && brokerKnown && flagsReady
  // During render, not in an effect: a crash in the chosen flow's first render commits no effect.
  if (isDecided) markOrderFlowChosen()

  // Which flow the embed opened and how long it waited for the broker, once per mount.
  useEffect(() => {
    if (!isDecided) return
    logger.info('embed purchase flow chosen', undefined, {
      context: {
        component: 'EmbedPurchaseFlowSwitch',
        action: 'chooseFlow',
        metadata: { flow: allowPfRevamp ? 'v2' : 'legacy', waitedMs: Date.now() - (waitStartedAt ?? Date.now()) },
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isDecided])

  if (status === 'error') return <EmbedSessionError />
  if (!brokerKnown || !flagsReady) return <LoadingPage />
  return allowPfRevamp ? <EmbedPurchaseFlowGuard component={PurchaseFlowRevamp} /> : <LegacyEmbedPurchaseFlow />
}
